import {
  getIndexAbove,
  getIndexBetween,
  IndexKey,
  sortByIndex,
  TLShapeId,
  useEditor,
  useValue,
} from "tldraw"
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  DragStartEvent,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core"
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable"
import { ReactNode, useCallback, useMemo } from "react"
import { restrictToVerticalAxis } from "@dnd-kit/modifiers"
import { last } from "../util/array"
import { Toolbar } from "./Toolbar"
import { Plus } from "lucide-react"
import { useCurrentPage } from "../hooks/useCurrentPage"
import { MenuButton } from "./MenuButton"
import ListItemIcon from "@mui/material/ListItemIcon"
import ListItemText from "@mui/material/ListItemText"
import MenuItem from "@mui/material/MenuItem"
import { PopoverOrigin } from "@mui/material/Popover"
import { Stroke } from "../icons/Stroke"
import { Fill } from "../icons/Fill"
import { Expr } from "../../shared/Expr"
import { CustomDrawShapeProps } from "../../shared/CustomDrawShape"
import { ToolbarSliverButton } from "./ToolbarButton"
import { LayerButton2 } from "./LayerButton2"
import { LDGroupShape, LDShape } from "../../shared/LDShape"

type LayersToolbarProps = {
  onSelectLayer: (layerId: string) => void
  onSelectedLayerClick: (layerId: string) => void
}

export function LayersToolbar2({
  onSelectLayer,
  onSelectedLayerClick,
}: LayersToolbarProps) {
  const editor = useEditor()

  // const page = useValue('page', () => editor.getCurrentPage(), [editor])
  const page = useCurrentPage()

  const selectedShapes = useValue(
    "selected shapes",
    () => editor.getSelectedShapes() as LDShape[],
    [editor],
  )

  // const layerCount = useValue('layer count', () => (editor.getCurrentPage().meta as PageMeta).layers.length, [editor])

  // const pointerSensor = useSensor(PointerSensor, {
  //   activationConstraint: {
  //     delay: 250,
  //     tolerance: 20,
  //   }
  // })
  const mouseSensor = useSensor(MouseSensor, {
    activationConstraint: {
      distance: 10,
      // delay: 250,
      // tolerance: 10,
    },
  })
  const touchSensor = useSensor(TouchSensor, {
    activationConstraint: {
      // delay: 250,
      // tolerance: 10,
      distance: 22,
    },
  })

  const sensors = useSensors(mouseSensor, touchSensor)

  if (!page.meta.layers) return null

  const sortedLayers = useMemo(
    () => [...Object.values(page.meta.layers)].sort(sortByIndex),
    [page.meta.layers],
  )

  // for rendering toolbar, we want the later layers to be rendered first in the toolbar (stack)
  const sortedLayersReverse = useMemo(
    () => [...sortedLayers].reverse(),
    [sortedLayers],
  )

  function handleHover(layerId: string | null): void {
    editor.updatePage({
      id: page.id,
      meta: { ...page.meta, focusedLayerId: layerId },
    })
  }

  const handleAddLayerClick = useCallback(
    function handleAddLayerClick() {
      editor.run(function () {
        editor.markHistoryStoppingPoint()

        const newLayerId = `layer-${sortedLayers.length}`

        const newLayer = {
          ...page.meta.layers[page.meta.currentLayer],
          index: getIndexAbove(last(sortedLayers)!.index),
          id: newLayerId,
        }
        // First update the page with the new layer
        editor.updatePage({
          id: page.id,
          name: `Layer ${sortedLayers.length}`,
          meta: {
            ...page.meta,
            layers: {
              ...page.meta.layers,
              [newLayerId]: newLayer,
            },
            currentLayer: newLayerId,
          },
        })

        // Then optionally add the color parameter if it's a fill

        close()
      })
    },
    [editor, sortedLayers, page],
  )

  function handleDragEnd(event: DragEndEvent) {
    editor.markHistoryStoppingPoint()
    ////console.log('drag end', event)

    if (!event.over || !event.over.id) return
    if (event.active.id === event.over?.id) return

    editor.run(function () {
      const layers = page.meta.layers

      const activeNumericIdx = sortedLayers.findIndex(
        (layer) => layer === layers[event.active.id],
      )
      let newNumericIdx = sortedLayers.findIndex(
        (layer) => layer === layers[event.over!.id],
      )
      if (activeNumericIdx < newNumericIdx) newNumericIdx++
      const prevLayer =
        newNumericIdx === 0 ? null : sortedLayers[newNumericIdx - 1]
      const nextLayer = sortedLayers[newNumericIdx]

      ////console.log(prevLayer, nextLayer)

      const newIndex = getIndexBetween(prevLayer?.index, nextLayer?.index)

      ////console.log(newIndex)

      // re-order the layer indices
      const newLayers = {
        ...layers,
        [event.active.id]: {
          ...layers[event.active.id],
          index: newIndex,
        },
      }
      editor.updatePage({
        id: page.id,
        meta: { ...page.meta, layers: newLayers },
      })

      // update any existing shapes
      const pageShapeIds = editor.getPageShapeIds(page.id)

      const shapeUpdates: { id: TLShapeId; index: IndexKey }[] = []

      for (const shapeId of pageShapeIds) {
        const shape = editor.getShape(shapeId)
        if (!shape || !["custom-draw", "sticker"].includes(shape.type)) continue

        const shapeProps = shape.props as CustomDrawShapeProps

        if (shapeProps.layer === event.active.id) {
          shapeUpdates.push({
            id: shapeId,
            index: newIndex,
          })
        }
      }

      editor.updateShapes(shapeUpdates as never[])
    })
  }

  function handleDragStart(event: DragStartEvent) {}

  return (
    <Toolbar direction="vertical" padded={false}>
      {/* <ToolbarSliverButton icon={Plus} onClick={handleAddLayerClick} /> */}

      <DndContext
        autoScroll={false}
        modifiers={[restrictToVerticalAxis]}
        onDragEnd={handleDragEnd}
        onDragStart={handleDragStart}
        sensors={sensors}
        collisionDetection={closestCenter}
        // consider: onDragOver={handleDragEnd}
      >
        <SortableContext
          items={sortedLayersReverse.map((layer) => `${layer.id}`)}
          strategy={verticalListSortingStrategy}
        >
          <LayersContainer>
            {sortedLayersReverse.map(function (layer) {
              const selectedLayers = selectedShapes.map((shape) =>
                LDShape.getLayerId(shape),
              )
              // If the layer is selected, we want to highlight it
              const isSelected = selectedLayers.includes(layer.id)

              return (
                <LayerButton2
                  key={`layer-${layer.id}`}
                  layerId={layer.id}
                  visible={Expr.evaluate(
                    layer.visible,
                    page.meta.parameterValues,
                  )}
                  isActive={layer.id === page.meta.currentLayer}
                  isSelected={isSelected}
                  onSelectedClick={onSelectedLayerClick}
                  onSelected={onSelectLayer}
                  onHover={handleHover}
                />
              )
            })}
          </LayersContainer>
        </SortableContext>
      </DndContext>
    </Toolbar>
  )
}

type LayersContainerProps = {
  children: ReactNode
}

export function LayersContainer({ children }: LayersContainerProps) {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        position: "relative",
        touchAction: "none",
      }}
    >
      {children}
    </div>
  )
}

const addLayerAnchorOrigin: PopoverOrigin = {
  vertical: "top",
  horizontal: "right",
}
